<template>
  <div id="app">

    <div
      v-if="loadingState == null || authLoadingState == null"
      class="ui active dimmer">
      <div class="ui loader"/>
    </div>

    <div v-if="loadingState !== null && authLoadingState !== null">
      <transition
        :name="transitionStyle"
        mode="out-in">
        <router-view
          @refetchRecurringStudies="getRecurringStudies"/>
      </transition>
    </div>


  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'App',
  data: function () {
    return {
      loadingState: null,
      authLoadingState: null,
      localUserAuthToken: undefined,
      transitionStyle: 'fade'
    };
  },
  computed: {
    ...mapState(['userCountry', 'userChronicity, userProphy, userMigraineYears'])
  },
  watch: {
    userAuthToken: function (value) {
      this.authLoadingState = null
      // whenever userAuthToken value changes, we will fetch the QnResponse
      if (value !== null && value !== undefined) {
        this.getQnResponseOverall().then(() => {
          if (this.userLang === 'en' || this.$route.fullPath.includes('square-study-recurring')) {
            this.getRecurringStudies().then(() => {
              this.authLoadingState = 'done'
            })
          } else {
            this.authLoadingState = 'done'
          }
        })
      }
    },
    '$route.name' (newRoute, oldRoute) {
      // TODO: Wrap this in a query about app version!!!
      if (newRoute === 'BaselineProphyPage') {
        this.transitionStyle = 'slide';
      } else if (oldRoute === 'BaselineProphyPage') {
        this.transitionStyle = 'slide-away';
      } else {
        this.transitionStyle = 'fade';
      }
    }
  },
  created () {
    this.setRollbarConfig();

    let payload = {
      userLang: this.getUserUrlParam('lang'),
      userCountry: this.getUserUrlParam('country'),
      userId: this.getUserUrlParam('uid')
    };

    this.updateUserProfile(payload)
      .then(
        () => { this.getUserAuthToken(); }
      );

    amplitude.getInstance().init(process.env.AMP_KEY);
    amplitude.getInstance().setUserId(this.userId);
    amplitude.getInstance().setUserProperties({
      'QuestionnaireBuddyView': true,
      'QuestionnaireBuddyVersion': process.env.NODE_ENV
    });

    amplitude.getInstance().logEvent('QB - View Page', { 'View': true });

    // TODO: Rewrite mixin `getUserUrlParam()`
    // REF: https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
    let match;
    let pl = /\+/g; // Regex for replacing addition symbol with a space
    let search = /([^&=]+)=?([^&]*)/g;
    let decode = function (s) { return decodeURIComponent(s.replace(pl, ' ')); };
    let query = window.location.search.substring(1);

    let allQueryStrings = {};
    while (match = search.exec(query)) { allQueryStrings[decode(match[1])] = decode(match[2]); }

    switch (this.getUserUrlParam('action')) {
      case 'seegrouptab':
        this.loadingState = 'done';
        this.$router.push({ name: 'Home', params: { initialTab: 1 }, query: { uid: this.getUserUrlParam('uid'), lang: this.getUserUrlParam('lang'), country: this.getUserUrlParam('country') } })
        break;

      case 'seeresults':
        this.loadingState = 'done';
        this.$router.push({
          name: 'Results',
          params: {
            formId: this.getUserUrlParam('formid')
          },
          query: allQueryStrings
        });
        break;

      case 'startquestionnaire':
        this.loadingState = 'done';
        amplitude.getInstance().logEvent('QB - Start Questionnaire', { 'Start': this.getUserUrlParam('formid') });
        amplitude.getInstance().setUserProperties({
          'QuestionnaireBuddyStart': true
        });
        this.$router.push({
          name: 'Questionnaire',
          params: {
            formId: this.getUserUrlParam('formid')
          },
          query: allQueryStrings
        });
        break;

      case 'starttypeform':
        amplitude.getInstance().logEvent('QB - Start Questionnaire', { 'Start': this.getUserUrlParam('formid') });
        amplitude.getInstance().setUserProperties({
          'QuestionnaireBuddyStart': true
        });
        window.location.href = 'https://healint.typeform.com/to/' + this.getUserUrlParam('formid') + '?userid=' + this.getUserUrlParam('uid') + '&lang=' + this.getUserUrlParam('lang') + '&version=' + process.env.NODE_ENV + '&country=' + this.getUserUrlParam('country');
        return;

      default:
        this.loadingState = 'done';
        if (this.getUserUrlParam('action') !== null) {
          if (process.env.NODE_ENV === 'production') {
            Rollbar.error('Illegal action being passed: ' + this.getUserUrlParam('action'));
          } else {
            console.log('Action type is not accounted for: ' + this.getUserUrlParam('action'));
          }
        }
    }
  },
  methods: {
    ...mapActions(['updateUserProfile', 'updateUserAuthToken', 'updateQnResponseOverall', 'updateUserSegmentation', 'updateRecurringResponseOverall']),
    setRollbarConfig () {
      window._rollbarConfig = {
        accessToken: 'c18d69c213c94ce48304d8fd34ebefc4',
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: process.env.NODE_ENV
        }
      };
    },
    getUserAuthToken () {
      let environmentVariable = process.env.APP_USERAUTHTOKEN;
      let urlParameter = this.getUserUrlParam('userAuthToken');

      if (process.env.NODE_ENV === 'development') {
        if (!environmentVariable && !urlParameter) { this.throwMissingAuthTokenError() }
        if (environmentVariable) { this.setAuthToken(environmentVariable); }
        if (urlParameter) { this.setAuthToken(urlParameter); }
      } else {
        this.listenForJSBridgeCustomEvents()
      }
    },
    throwMissingAuthTokenError () {
      let errorMessage = `MISSING USER AUTHENTICATION TOKEN: Add the token as development environment variable "APP_USERAUTHTOKEN" or as URL parameter "userAuthToken". For URL parameter, token must be URL encoded.`;
      alert(errorMessage);
      throw errorMessage;
    },
    setAuthToken (authToken) {
      window.userAuthToken = authToken;
      this.localUserAuthToken = authToken;
      this.updateUserAuthToken(authToken);
    },
    listenForJSBridgeCustomEvents () {
      window.addEventListener(
        'userAuthToken',
        event => { this.setAuthToken(event.detail); },
        { once: true }
      );

      window.addEventListener(
        'JSBridgeIsLoaded',
        () => { this.getUserAuthTokenFromJSBridge(); },
        { once: true }
      );
    },
    getUserAuthTokenFromJSBridge () {
      let Device = {
        iOS: function () {
          return /iPhone|iPad|iPod/i.test(navigator.userAgent);
        }
      };

      let successCallback = responseData => {
        let event = new CustomEvent('userAuthToken', { detail: JSON.parse(responseData).AUTH_TOKEN });
        window.dispatchEvent(event);
      };

      let errorCallback = (responseData, jsonError) => {
        let error = JSON.parse(jsonError);
        Rollbar.error('Failed to fetch `authToken` via JS Bridge', error);
      };

      // Assigns both response to `window.userAuthToken` as Android and iOS deals with Promise object differently
      if (Device.iOS()) {
        getAuthToken(successCallback, null);
      } else {
        getAuthToken(successCallback, errorCallback);
      }
    },
    getQnResponseOverall () {
      let requestUrl = process.env.API_URL + 'user/?userid=' + this.userId + '&lang=' + this.userLang + '&country=' + this.userCountry;

      return new Promise((resolve, reject) => {
        this.$http.get(this.authUrl, {
          headers: {
            'request-url': requestUrl
          }})
          .then((response) => {
            let jsonResponse = JSON.parse(response.body);
            let filteredForms = jsonResponse.forms.filter(form => form.preview_only === false);

            Promise.all([this.updateUserSegmentation(jsonResponse.segments), this.updateQnResponseOverall(filteredForms)]).then(() => {
              this.loadingState = 'done';
              resolve()
            })
          })
          .catch(error => {
            Rollbar.error(`Couldn't get question responses for userid '${this.userId}':`, error)
            reject(error)
          })
      })

    },
    getRecurringStudies () {
      let requestUrl = process.env.API_URL + 'user/studies/recurring?userid=' + this.userId + '&lang=' + this.userLang + '&country=' + this.userCountry;

      return new Promise((resolve, reject) => {
        this.$http.get(this.authUrl, {
          headers: {
            'request-url': requestUrl
          }})
          .then((response) => {
            let jsonResponse = JSON.parse(response.body);

            if (jsonResponse.hasOwnProperty('result')) {
              this.updateRecurringResponseOverall(jsonResponse.result)
            }

            resolve()
          })
          .catch(error => {
            Rollbar.error(`Error fetching recurring studies for '${this.userId}': ${error.message}`)
            reject(error)
          })
      })
    }
  }
};
</script>

<style>
  body {
    /*background-color: #3e5c73 !important;*/
    max-width: 100vw;
    background-color: #263d4b !important;
    color: white !important;
    -webkit-touch-callout:none;
    -webkit-tap-highlight-color:rgba(0,0,0,0) !important;
    -webkit-user-select:none;
  }

  .primary {
    background-color: #26deca !important;
    color: white !important;
  }

  .primary-text {
    color: #26deca !important;
  }

  .secondary {
    background-color: #3e838a !important;
    color: white !important;
  }

  .tertiary {
    background-color: #6496BB !important;
    color: white !important;
  }

  .ui.inverted.segment {
    background-color: #222a34 !important;
  }

  .mdl-card {
    border-top-left-radius: 0.3em !important;
    border-radius: 0.3em !important;
  }

  .mdl-card__title {
    align-items: flex-end;
  }

  .mdl-card__title.unanswered {
    width: 100%;
    color: #fff;
    text-shadow: 2px 2px grey;
    height: 10em;
  }

  .mdl-card__title.answered {
    width: 100%;
    color: #fff;
    text-shadow: 1px 1px grey;
    height: auto;
    background-color: #3e5c73;
  }

  .mdl-card__title.thankyou {
    width: 100%;
    color: #fff;
    text-shadow: 2px 2px grey;
    height: 10em;
    background: url('../static/assets/healthcaresurvey.png') center / cover;
  }

  .mdl-card__title.locked {
    width: 100%;
    color: #fff;
    text-shadow: 1px 1px grey;
    height: 5em;
    background-color: #3e5c73 !important;
  }

  /*Vue Animation Classes*/
  .fade-enter-active {
    transition: opacity 0.2s ease !important;
  }

  .fade-leave-active {
    transition: opacity 0.15s ease !important;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  /* For routes that should have slide and slide-away, need to set up a props and bind it to the prop */
  .slide-enter-active {
    transition: all .15s ease;
  }
  .slide-leave-active {
    transition: all .1s ease;
  }
  .slide-enter {
    transform: translateX(100vw);
    opacity: 0;
  }
  .slide-leave-to{
    opacity: 0;
  }

  .slide-away-enter-active {
    transition: all .1s ease;
  }
  .slide-away-leave-active {
    transition: all .2s ease;
  }
  .slide-away-enter {
    opacity: 0;
  }
  .slide-away-leave-to {
    transform: translateX(100vw);
    opacity: 1;
  }
</style>



// WEBPACK FOOTER //
// src/App.vue